<script setup lang="ts">
import { atIndexMaybe, isJust, isNothing, just, nothing } from "../lib/maybe";
import type {
  ZwDatabaseFieldDef,
  ZwDatabaseFieldKind,
  ZwDatabaseRelationPair,
} from "../lib/zwConfig";
import {
  DATABASE_FIELD_KINDS,
  newDatabaseFieldId,
  newDatabaseRelationPairId,
} from "../lib/zwConfig";
import { customRelationId } from "../lib/entityLinks";
import ZwButton from "./ui/ZwButton.vue";
import ZwInput from "./ui/ZwInput.vue";
import ZwSelect from "./ui/ZwSelect.vue";

const fields = defineModel<ZwDatabaseFieldDef[]>("fields", {
  required: true,
});
const relationPairs = defineModel<ZwDatabaseRelationPair[]>("relationPairs", {
  required: true,
});

function addDatabaseField(): void {
  fields.value = [
    ...fields.value,
    { id: newDatabaseFieldId(), name: "", kind: "text", options: [] },
  ];
}

function updateDatabaseFieldName(index: number, name: string): void {
  const current = atIndexMaybe(fields.value, index);
  if (isNothing(current)) return;
  const next = [...fields.value];
  next[index] = { ...current.value, name };
  fields.value = next;
}

function updateDatabaseFieldKind(index: number, kind: string): void {
  const current = atIndexMaybe(fields.value, index);
  if (isNothing(current)) return;
  const next = [...fields.value];
  const resolvedKind = (
    DATABASE_FIELD_KINDS.some((k) => k.id === kind) ? kind : "text"
  ) as ZwDatabaseFieldKind;
  next[index] = {
    ...current.value,
    kind: resolvedKind,
    // Options only mean anything for `select`. Drop stale choices from a
    // previous kind so switching away and back starts clean.
    options: resolvedKind === "select" ? current.value.options : [],
  };
  fields.value = next;
}

function updateDatabaseFieldOptions(index: number, raw: string): void {
  const current = atIndexMaybe(fields.value, index);
  if (isNothing(current)) return;
  const options = raw
    .split(",")
    .map((o) => o.trim())
    .filter((o) => o.length > 0);
  const next = [...fields.value];
  next[index] = { ...current.value, options };
  fields.value = next;
}

function removeDatabaseField(index: number): void {
  fields.value = fields.value.filter((_, i) => i !== index);
}

function addDatabaseRelationPair(): void {
  relationPairs.value = [
    ...relationPairs.value,
    {
      id: newDatabaseRelationPairId(),
      aId: "",
      aLabel: "",
      bId: "",
      bLabel: "",
    },
  ];
}

function updateDatabaseRelationSide(
  index: number,
  side: "a" | "b",
  label: string,
): void {
  const current = atIndexMaybe(relationPairs.value, index);
  if (isNothing(current)) return;
  const next = [...relationPairs.value];
  const pair: ZwDatabaseRelationPair = { ...current.value };
  const id = customRelationId(label);
  if (side === "a") {
    pair.aLabel = label;
    if (pair.aId.length === 0) {
      pair.aId = isJust(id) ? id.value : "";
    }
  } else {
    pair.bLabel = label;
    if (pair.bId.length === 0) {
      pair.bId = isJust(id) ? id.value : pair.aId;
    }
  }
  next[index] = pair;
  relationPairs.value = next;
}

function removeDatabaseRelationPair(index: number): void {
  relationPairs.value = relationPairs.value.filter((_, i) => i !== index);
}
</script>

<template>
  <section class="project-settings-group" aria-label="Custom fields">
    <p class="project-settings-hint">
      Add details and relationships for your characters, places, and other
      worldbuilding entries.
    </p>

    <div class="project-settings-credits-head">
      <h3 class="project-settings-subhead">Fields</h3>
      <ZwButton size="sm" @click="addDatabaseField">Add field</ZwButton>
    </div>

    <div class="project-settings-database-fields">
      <div
        v-for="(field, index) in fields"
        :key="field.id"
        class="project-settings-database-field-row"
      >
        <ZwInput
          :id="nothing"
          :model-value="field.name"
          :label="nothing"
          :aria-label="`Field ${index + 1} name`"
          @update:model-value="updateDatabaseFieldName(index, $event)"
        />
        <ZwSelect
          :id="nothing"
          :model-value="field.kind"
          :label="nothing"
          :aria-label="`Field ${index + 1} type`"
          :options="
            just(
              DATABASE_FIELD_KINDS.map((k) => ({
                value: k.id,
                label: k.label,
              })),
            )
          "
          @update:model-value="updateDatabaseFieldKind(index, $event)"
        />
        <ZwInput
          v-if="field.kind === 'select'"
          :id="nothing"
          :model-value="field.options.join(', ')"
          :label="nothing"
          :aria-label="`Field ${index + 1} choices`"
          placeholder="Choices, comma-separated"
          @update:model-value="updateDatabaseFieldOptions(index, $event)"
        />
        <span v-else class="project-settings-database-field-spacer" />
        <ZwButton
          size="sm"
          variant="danger"
          class="project-settings-dictionary-remove"
          @click="removeDatabaseField(index)"
        >
          Remove
        </ZwButton>
      </div>
      <p v-if="fields.length === 0" class="project-settings-hint">
        No custom fields yet.
      </p>
    </div>
  </section>
  <section class="project-settings-group" aria-label="Relationships">
    <div class="project-settings-credits-head">
      <h3 class="project-settings-subhead">Relationships</h3>
      <ZwButton size="sm" @click="addDatabaseRelationPair">Add pair</ZwButton>
    </div>
    <p class="project-settings-hint">
      Connect two entries with labels such as “Child of” and “Parent of”. Leave
      the second label blank to use the same wording both ways, such as “Sibling
      of”.
    </p>

    <div class="project-settings-database-fields">
      <div
        v-for="(pair, index) in relationPairs"
        :key="pair.id"
        class="project-settings-database-field-row"
      >
        <ZwInput
          :id="nothing"
          :model-value="pair.aLabel"
          :label="nothing"
          :aria-label="`Pair ${index + 1} this side`"
          placeholder="This side (e.g. Child of)"
          @update:model-value="updateDatabaseRelationSide(index, 'a', $event)"
        />
        <ZwInput
          :id="nothing"
          :model-value="pair.bLabel"
          :label="nothing"
          :aria-label="`Pair ${index + 1} other side`"
          placeholder="Other side (optional)"
          @update:model-value="updateDatabaseRelationSide(index, 'b', $event)"
        />
        <span class="project-settings-database-field-spacer" />
        <ZwButton
          size="sm"
          variant="danger"
          class="project-settings-dictionary-remove"
          @click="removeDatabaseRelationPair(index)"
        >
          Remove
        </ZwButton>
      </div>
      <p v-if="relationPairs.length === 0" class="project-settings-hint">
        No custom relationship pairs yet.
      </p>
    </div>
  </section>
</template>
